import dayjs from 'dayjs';
import { CoverImageComponent } from '../../../components/misc/cover-image-component.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { isNotNullOrUndefined, isNullOrUndefined } from '../../../utility/null-safety.js';
import { SerializationContext, SerializedData } from '../../dto/data-model.js';
import { EventOpenAccessProtocol } from '../open-access-pass/event-open-access-protocol.js';
import { GymAccessProtocol, GymPassRenewalPeriod } from '../open-access-pass/gym-access-protocol.js';
import { LimitedTimeAccessProtocol } from '../open-access-pass/limited-time-access-protocol.js';
import { LimitedUseAccessProtocol } from '../open-access-pass/limited-use-access-protocol.js';
import { SeatedOpenAccessProtocol } from '../open-access-pass/seated-open-access-protocol.js';
import { OpenAccessPass, OpenAccessPassStatus } from '../open-access-pass/open-access-pass.js';
import { EventInfo } from '../event-info.js';
import { LocalizedString } from '../localized-string.js';
import { PhotoReference } from '../file-reference/photo-reference.js';
import { PhoneNumber } from '../phone-number.js';
import { JSONUtils } from '../../../utility/json-utils.js';

export class OpenAccessPassInfo {
  private constructor(
      public readonly names: LocalizedString,
      public readonly cover: PhotoReference | null,
      public readonly status: OpenAccessPassStatus,
      public readonly usedAt: dayjs.Dayjs | null,
  ) {}

  static create(args: {
    names: LocalizedString,
    cover: PhotoReference | null,
    status: OpenAccessPassStatus,
    usedAt: dayjs.Dayjs | null,
  }): OpenAccessPassInfo {
    return new OpenAccessPassInfo(
        args.names,
        args.cover,
        args.status,
        args.usedAt,
    );
  }

  static fromOpenAccessPass(pass: OpenAccessPass): OpenAccessPassInfo {
    const component = pass.findComponentByTypeId<CoverImageComponent>(CoverImageComponent.TYPE_ID);

    return OpenAccessPassInfo.create({
      names: pass.overrideName ?? pass.originalName,
      cover: component?.coverImage ?? null,
      status: pass.status,
      usedAt: pass.usedAt,
    });
  }

  static fromMap(context: SerializationContext, map: SerializedData): OpenAccessPassInfo {
    return OpenAccessPassInfo.create({
      names: context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'names')),
      cover: context.fromDataOrNull<PhotoReference>(MapUtils.getOrNull<SerializedData>(map, 'cover')),
      status: toEnumFromString(MapUtils.get<string>(map, 'status'), OpenAccessPassStatus),
      usedAt: context.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'usedAt')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'names': context.toData(this.names),
      'cover': context.toDataOrNull(this.cover),
      'status': fromEnumAsString(this.status),
      'usedAt': context.encodeDateOrNull(this.usedAt),
    };
  }
}

export class CustomerProfileInfo {
  private constructor(
      public readonly formattedName: string | null,
      public readonly email: string | null,
      public readonly phoneNumber: PhoneNumber | null,
  ) {}

  static create(args: {
    formattedName: string | null,
    email: string | null,
    phoneNumber: PhoneNumber | null,
  }): CustomerProfileInfo {
    return new CustomerProfileInfo(
        args.formattedName,
        args.email,
        args.phoneNumber,
    );
  }

  static fromOpenAccessPass(pass: OpenAccessPass): CustomerProfileInfo | null {
    if(isNotNullOrUndefined(pass.customer)) {
      return CustomerProfileInfo.create(pass.customer);
    }

    return null;
  }

  static fromMapOrNull(context: SerializationContext, map: SerializedData | null): CustomerProfileInfo | null {
    if(isNullOrUndefined(map)) {
      return null;
    }

    return CustomerProfileInfo.create({
      formattedName: MapUtils.getStringOrNull(map, 'formattedName'),
      email: MapUtils.getStringOrNull(map, 'email'),
      phoneNumber: PhoneNumber.decode(MapUtils.getOrNull<string>(map, 'phoneNumber')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'formattedName': this.formattedName,
      'email': this.email,
      'phoneNumber': this.phoneNumber?.encode() ?? null,
    };
  }
}

export class EventOpenAccessProtocolInfo {
  private constructor(
    public readonly events: EventInfo[] | null,
  ) {}

  static create(args: {
    events: EventInfo[] | null,
  }): EventOpenAccessProtocolInfo {
    return new EventOpenAccessProtocolInfo(
        args.events,
    );
  }

  static fromOpenAccessPass(pass: OpenAccessPass, events: EventInfo[] | null): EventOpenAccessProtocolInfo | null {
    if(EventOpenAccessProtocol.assertType(pass.protocol)) {
      return EventOpenAccessProtocolInfo.create({ events });
    }

    return null;
  }

  static fromMapOrNull(context: SerializationContext, map: SerializedData | null): EventOpenAccessProtocolInfo | null {
    if(isNullOrUndefined(map)) {
      return null;
    }

    return EventOpenAccessProtocolInfo.create({
      events: MapUtils.getList<EventInfo>(
          map,
          'events',
          (data) => EventInfo.fromMap(context, data as SerializedData),
      ),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'events': this.events?.map((event) => event.toMap(context)) ?? null,
    };
  }
}

export class SeatedOpenAccessProtocolInfo {
  private constructor(
    public readonly events: EventInfo[] | null,
    public readonly seat: LocalizedString | null,
  ) {}

  static create(args: {
    events: EventInfo[] | null,
    seat: LocalizedString | null,
  }): SeatedOpenAccessProtocolInfo {
    return new SeatedOpenAccessProtocolInfo(
        args.events,
        args.seat,
    );
  }

  static fromOpenAccessPass(pass: OpenAccessPass, events: EventInfo[] | null): SeatedOpenAccessProtocolInfo | null {
    if(SeatedOpenAccessProtocol.assertType(pass.protocol)) {
      return SeatedOpenAccessProtocolInfo.create({
        events: events,
        seat: pass.protocol.seatingOptions?.descriptions ?? null,
      });
    }

    return null;
  }

  static fromMapOrNull(context: SerializationContext, map: SerializedData | null): SeatedOpenAccessProtocolInfo | null {
    if(isNullOrUndefined(map)) {
      return null;
    }

    return SeatedOpenAccessProtocolInfo.create({
      events: MapUtils.getList<EventInfo>(
          map,
          'events',
          (data) => EventInfo.fromMap(context, JSONUtils.decodeRecord(data)),
      ),
      seat: context.fromDataOrNull<LocalizedString>(MapUtils.getOrNull<SerializedData>(map, 'seat')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'events': this.events?.map((event) => event.toMap(context)) ?? null,
      'seat': context.toDataOrNull(this.seat),
    };
  }
}

export class LimitedUseAccessProtocolInfo {
  private constructor(
      public readonly maxUses: number | null,
      public readonly uses: number | null,
  ) {}

  static create(args: {
    maxUses: number | null,
    uses: number | null,
  }): LimitedUseAccessProtocolInfo {
    return new LimitedUseAccessProtocolInfo(
        args.maxUses,
        args.uses,
    );
  }

  static fromOpenAccessPass(pass: OpenAccessPass): LimitedUseAccessProtocolInfo | null {
    if(LimitedUseAccessProtocol.assertType(pass.protocol)) {
      return LimitedUseAccessProtocolInfo.create(pass.protocol);
    }

    return null;
  }

  static fromMapOrNull(context: SerializationContext, map: SerializedData | null): LimitedUseAccessProtocolInfo | null {
    if(isNullOrUndefined(map)) {
      return null;
    }

    return LimitedUseAccessProtocolInfo.create({
      maxUses: MapUtils.getNumberOrNull(map, 'maxUses'),
      uses: MapUtils.getNumberOrNull(map, 'uses'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'maxUses': this.maxUses,
      'uses': this.uses,
    };
  }
}

export class LimitedTimeAccessProtocolInfo {
  private constructor(
      public readonly expirationDate: dayjs.Dayjs,
  ) {}

  static create(args: {
    expirationDate: dayjs.Dayjs,
  }): LimitedTimeAccessProtocolInfo {
    return new LimitedTimeAccessProtocolInfo(
        args.expirationDate,
    );
  }

  static fromOpenAccessPass(pass: OpenAccessPass): LimitedTimeAccessProtocolInfo | null {
    if(LimitedTimeAccessProtocol.assertType(pass.protocol)) {
      return LimitedTimeAccessProtocolInfo.create(pass.protocol);
    }

    return null;
  }

  static fromMapOrNull(context: SerializationContext, map: SerializedData | null): LimitedTimeAccessProtocolInfo | null {
    if(isNullOrUndefined(map)) {
      return null;
    }

    return LimitedTimeAccessProtocolInfo.create({
      expirationDate: context.decodeDate(MapUtils.getNumber(map, 'expirationDate')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'expirationDate': context.encodeDate(this.expirationDate),
    };
  }
}

export class GymAccessProtocolInfo {
  private constructor(
      public readonly maxUses: number | null,
      public readonly uses: number | null,
      public readonly expirationDate: dayjs.Dayjs,
      public readonly renewalDate: dayjs.Dayjs,
      public readonly renewalPeriod: GymPassRenewalPeriod,
      public readonly isUsed: boolean,
  ) {}

  static create(args: {
    maxUses: number | null,
    uses: number | null,
    expirationDate: dayjs.Dayjs,
    renewalDate: dayjs.Dayjs,
    renewalPeriod: GymPassRenewalPeriod,
    isUsed: boolean,
  }): GymAccessProtocolInfo {
    return new GymAccessProtocolInfo(
        args.maxUses,
        args.uses,
        args.expirationDate,
        args.renewalDate,
        args.renewalPeriod,
        args.isUsed,
    );
  }

  static fromOpenAccessPass(pass: OpenAccessPass): GymAccessProtocolInfo | null {
    if(GymAccessProtocol.assertType(pass.protocol)) {
      return GymAccessProtocolInfo.create(pass.protocol);
    }

    return null;
  }

  static fromMapOrNull(context: SerializationContext, map: SerializedData | null): GymAccessProtocolInfo | null {
    if(isNullOrUndefined(map)) {
      return null;
    }

    return GymAccessProtocolInfo.create({
      maxUses: MapUtils.getNumberOrNull(map, 'maxUses'),
      uses: MapUtils.getNumberOrNull(map, 'uses'),
      expirationDate: context.decodeDate(MapUtils.getNumber(map, 'expirationDate')),
      renewalDate: context.decodeDate(MapUtils.getNumber(map, 'renewalDate')),
      renewalPeriod: toEnumFromString(MapUtils.get<string>(map, 'renewalPeriod'), GymPassRenewalPeriod),
      isUsed: MapUtils.getBoolean(map, 'isUsed'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'maxUses': this.maxUses,
      'uses': this.uses,
      'expirationDate': context.encodeDate(this.expirationDate),
      'renewalDate': context.encodeDate(this.renewalDate),
      'renewalPeriod': fromEnumAsString(this.renewalPeriod),
      'isUsed': this.isUsed,
    };
  }
}

export class NfcPassPageInfo {
  private constructor(
    public readonly pass: OpenAccessPassInfo,
    public readonly customer: CustomerProfileInfo | null,
    public readonly eventAccessInfo: EventOpenAccessProtocolInfo | null,
    public readonly seatingAccessInfo: SeatedOpenAccessProtocolInfo | null,
    public readonly useAccessInfo: LimitedUseAccessProtocolInfo | null,
    public readonly timeAccessInfo: LimitedTimeAccessProtocolInfo | null,
    public readonly gymAccessInfo: GymAccessProtocolInfo | null,
  ) {}

  static create(args: {
    pass: OpenAccessPassInfo,
    customer: CustomerProfileInfo | null,
    eventAccessInfo: EventOpenAccessProtocolInfo | null,
    seatingAccessInfo: SeatedOpenAccessProtocolInfo | null,
    useAccessInfo: LimitedUseAccessProtocolInfo | null,
    timeAccessInfo: LimitedTimeAccessProtocolInfo | null,
    gymAccessInfo: GymAccessProtocolInfo | null,
  }) {
    return new NfcPassPageInfo(
        args.pass,
        args.customer,
        args.eventAccessInfo,
        args.seatingAccessInfo,
        args.useAccessInfo,
        args.timeAccessInfo,
        args.gymAccessInfo,
    );
  }

  static fromOpenAccessPass(pass: OpenAccessPass, events: EventInfo[] | null): NfcPassPageInfo {
    return NfcPassPageInfo.create({
      pass: OpenAccessPassInfo.fromOpenAccessPass(pass),
      customer: CustomerProfileInfo.fromOpenAccessPass(pass),
      eventAccessInfo: EventOpenAccessProtocolInfo.fromOpenAccessPass(pass, events),
      seatingAccessInfo: SeatedOpenAccessProtocolInfo.fromOpenAccessPass(pass, events),
      useAccessInfo: LimitedUseAccessProtocolInfo.fromOpenAccessPass(pass),
      timeAccessInfo: LimitedTimeAccessProtocolInfo.fromOpenAccessPass(pass),
      gymAccessInfo: GymAccessProtocolInfo.fromOpenAccessPass(pass),
    });
  }

  static fromMapOrNull(context: SerializationContext, map: SerializedData | null): NfcPassPageInfo | null {
    if(isNullOrUndefined(map)) {
      return null;
    }

    return NfcPassPageInfo.create({
      pass: OpenAccessPassInfo.fromMap(context, MapUtils.get<SerializedData>(map, 'pass')),
      customer: CustomerProfileInfo.fromMapOrNull(context, MapUtils.getOrNull<SerializedData>(map, 'customer')),
      eventAccessInfo: EventOpenAccessProtocolInfo.fromMapOrNull(context, MapUtils.getOrNull<SerializedData>(map, 'eventAccessInfo')),
      seatingAccessInfo: SeatedOpenAccessProtocolInfo.fromMapOrNull(context, MapUtils.getOrNull<SerializedData>(map, 'seatingAccessInfo')),
      useAccessInfo: LimitedUseAccessProtocolInfo.fromMapOrNull(context, MapUtils.getOrNull<SerializedData>(map, 'useAccessInfo')),
      timeAccessInfo: LimitedTimeAccessProtocolInfo.fromMapOrNull(context, MapUtils.getOrNull<SerializedData>(map, 'timeAccessInfo')),
      gymAccessInfo: GymAccessProtocolInfo.fromMapOrNull(context, MapUtils.getOrNull<SerializedData>(map, 'gymAccessInfo')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'pass': this.pass?.toMap(context) ?? null,
      'customer': this.customer?.toMap(context) ?? null,
      'eventAccessInfo': this.eventAccessInfo?.toMap(context) ?? null,
      'seatingAccessInfo': this.seatingAccessInfo?.toMap(context) ?? null,
      'useAccessInfo': this.useAccessInfo?.toMap(context) ?? null,
      'timeAccessInfo': this.timeAccessInfo?.toMap(context) ?? null,
      'gymAccessInfo': this.gymAccessInfo?.toMap(context) ?? null,
    };
  }
}
